(function () {
  'use strict';

  /**
   * Drives the utility bar's country/flag dropdown
   * (snippets/utility-bar-country-selector.liquid). Mirrors
   * initLangSelector in assets/header-redesign.js (the header's own
   * language dropdown) exactly, adapted for country codes: the dropdown
   * always lists the same fixed 3 countries (Italy/Germany/Spain),
   * independent of how many markets are actually configured in Shopify
   * admin.
   *
   * For whichever of the 3 IS a real, available Shopify market
   * (data-country-available="true", i.e. present in
   * localization.available_countries), selecting it sets the real,
   * hidden <input name="country_code"> inside {% form 'localization' %}
   * and submits that form - Shopify's own native mechanism, so the
   * storefront genuinely switches market/currency. No separate/fake
   * localization system for that case.
   *
   * For any of the 3 NOT yet added as a real Shopify market (a merchant
   * setup step in Settings > Markets, not something this script can
   * fabricate), selecting it just updates the displayed flag and
   * remembers the choice, without submitting the form or navigating.
   *
   * Either way, the chosen flag is persisted in localStorage
   * (COUNTRY_STORAGE_KEY) and re-applied on every page load, so the
   * visual selection survives refreshes and navigation regardless of
   * Shopify's own market state.
   */
  var COUNTRY_STORAGE_KEY = 'utilityBarCountryPreference';

  function readStoredCountry() {
    try {
      return window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeStoredCountry(code) {
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, code);
    } catch (e) {
      /* localStorage unavailable (private browsing, disabled, etc.) -
         the flag/dropdown still work for the current page, they just
         won't remember the choice on the next visit. */
    }
  }

  function closeDropdown(root) {
    var trigger = root.querySelector('[data-morveal-utility-country-trigger]');
    var dropdown = root.querySelector('[data-morveal-utility-country-dropdown]');
    if (!dropdown) return;
    dropdown.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function openDropdown(root) {
    var trigger = root.querySelector('[data-morveal-utility-country-trigger]');
    var dropdown = root.querySelector('[data-morveal-utility-country-dropdown]');
    if (!dropdown) return;
    dropdown.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function applyFlag(root, option) {
    var currentFlag = root.querySelector('[data-morveal-utility-current-flag]');
    var flagSrc = option.getAttribute('data-flag-src');
    var countryName = option.getAttribute('data-country-name');
    if (currentFlag) {
      if (flagSrc) currentFlag.setAttribute('src', flagSrc);
      if (countryName) currentFlag.setAttribute('alt', countryName);
    }

    var code = option.getAttribute('data-country-code');
    var options = Array.prototype.slice.call(root.querySelectorAll('[data-morveal-utility-country-option]'));
    options.forEach(function (o) {
      var isMatch = code ? o.getAttribute('data-country-code') === code : o === option;
      o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
  }

  function initCountrySelector(root) {
    if (root.dataset.utilityCountryInitialized === 'true') return;
    root.dataset.utilityCountryInitialized = 'true';

    var trigger = root.querySelector('[data-morveal-utility-country-trigger]');
    var dropdown = root.querySelector('[data-morveal-utility-country-dropdown]');
    var input = root.querySelector('[data-morveal-utility-country-input]');
    if (!trigger || !dropdown) return;

    var options = Array.prototype.slice.call(root.querySelectorAll('[data-morveal-utility-country-option]'));

    var storedCode = readStoredCountry();
    if (storedCode) {
      var storedOption = options.find(function (o) {
        return o.getAttribute('data-country-code') === storedCode;
      });
      if (storedOption) applyFlag(root, storedOption);
    }

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      if (dropdown.hidden) {
        openDropdown(root);
      } else {
        closeDropdown(root);
      }
    });

    document.addEventListener('click', function (event) {
      if (!dropdown.hidden && !root.contains(event.target)) {
        closeDropdown(root);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !dropdown.hidden) {
        closeDropdown(root);
        trigger.focus();
      }
    });

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        var code = option.getAttribute('data-country-code');
        var available = option.getAttribute('data-country-available') === 'true';

        applyFlag(root, option);
        if (code) writeStoredCountry(code);
        closeDropdown(root);

        if (available && input && code) {
          input.value = code;
          if (input.form) input.form.submit();
        }
      });
    });
  }

  function initAll() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-morveal-utility-country]'));
    roots.forEach(initCountrySelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  /**
   * Moves the utility bar's Order Status/Gift Cards/Help/country-
   * selector links (.morveal-utility-bar__links, from the desktop
   * utility bar strip) into the hamburger nav drawer's own slot
   * (snippets/site-mobile-nav.liquid's [data-utility-mobile]) at
   * mobile widths, and moves the same real node back to its original
   * spot in the desktop strip above that width - a real DOM move, not
   * a clone/second render, so there is exactly one copy of this
   * content (and its one real country-selector instance/listeners)
   * at all times, never both places at once.
   *
   * This is JS-driven rather than a second Liquid render because
   * section.settings can't be read across section-group members
   * (sections['morveal-utility-bar'] resolves blank even from within
   * that section's own template - confirmed live) - so there's no way
   * for a template rendered from a different section (the header's
   * own mobile nav drawer) to independently reproduce this same,
   * correctly-configured content. Moving the actual already-rendered
   * node sidesteps that entirely.
   */
  var MOBILE_BREAKPOINT_QUERY = '(max-width: 1023px)';

  function initMobileUtilityLinksMove() {
    var links = document.querySelector('.morveal-utility-bar__links');
    var mobileSlot = document.querySelector('[data-utility-mobile]');
    if (!links || !mobileSlot) return;

    var originalParent = links.parentNode;
    var originalNextSibling = links.nextSibling;
    var mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);

    function apply() {
      if (mediaQuery.matches) {
        if (links.parentNode !== mobileSlot) {
          mobileSlot.appendChild(links);
        }
        links.classList.add('morveal-utility-bar__links--mobile');
      } else {
        if (links.parentNode !== originalParent) {
          originalParent.insertBefore(links, originalNextSibling);
        }
        links.classList.remove('morveal-utility-bar__links--mobile');
      }
    }

    apply();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', apply);
    } else if (mediaQuery.addListener) {
      /* Safari <14 fallback. */
      mediaQuery.addListener(apply);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileUtilityLinksMove);
  } else {
    initMobileUtilityLinksMove();
  }
})();
